import isEqual from 'lodash/isEqual'

import type { BoardComponentDiff } from './board-version-type.js'

/**
 * 보드 model(things-scene 컴포넌트 트리) diff 순수 로직 (DB 접근 없음 — 단위 테스트 용이).
 *
 * 컴포넌트 identity 는 `refid`(things-scene 참조 id) 기준. 없으면 부모 경로+타입+인덱스로 보정.
 * 시나리오 스텝 diff 와 동형이나, 배열이 아니라 재귀 트리를 평면화해 비교한다.
 */

interface FlatComponent {
  key: string
  refid: number | null
  type: string | undefined
  component: any
}

function safeParseModel(model: any): any {
  if (model === null || model === undefined) return null
  if (typeof model !== 'string') return model
  try {
    return JSON.parse(model)
  } catch (e) {
    return null
  }
}

function walk(components: any[], parentPath: string, out: FlatComponent[]): void {
  if (!Array.isArray(components)) return
  components.forEach((c: any, i: number) => {
    const refid = typeof c?.refid === 'number' ? c.refid : null
    const key = refid !== null ? `refid:${refid}` : `path:${parentPath}/${c?.type || '?'}#${i}`
    out.push({ key, refid, type: c?.type, component: c })
    if (Array.isArray(c?.components)) {
      walk(c.components, key, out)
    }
  })
}

export function flattenBoardComponents(model: any): FlatComponent[] {
  const root = safeParseModel(model)
  const out: FlatComponent[] = []
  if (root && typeof root === 'object') {
    // 루트(scene) 자체 속성도 diff 대상 — 배경색·조명·scale·translate 등 scene 레벨 변경.
    // (기존엔 components 만 비교해 배경/조명만 바뀐 릴리즈가 "no difference" 로 나오던 버그.)
    out.push({ key: '__root__', refid: null, type: root.type || 'scene', component: root })
    if (Array.isArray(root.components)) {
      walk(root.components, '', out)
    }
  }
  return out
}

/** 컴포넌트의 자체 속성 diff — children(components) 는 트리 구조로 별도 비교하므로 제외. */
function changedProps(base: any, target: any): string[] {
  const keys = new Set<string>([...Object.keys(base || {}), ...Object.keys(target || {})])
  keys.delete('components')
  const changed: string[] = []
  for (const key of keys) {
    if (!isEqual(base?.[key], target?.[key])) {
      changed.push(key)
    }
  }
  return changed.sort()
}

export function diffBoardModels(baseModel: any, targetModel: any): BoardComponentDiff[] {
  const base = flattenBoardComponents(baseModel)
  const target = flattenBoardComponents(targetModel)

  const baseMap = new Map<string, FlatComponent>(base.map(f => [f.key, f]))
  const targetMap = new Map<string, FlatComponent>(target.map(f => [f.key, f]))

  const diffs: BoardComponentDiff[] = []

  for (const [key, f] of baseMap) {
    if (!targetMap.has(key)) {
      diffs.push({ refid: f.refid, type: f.type, changeType: 'removed' })
    }
  }

  for (const [key, f] of targetMap) {
    if (!baseMap.has(key)) {
      diffs.push({ refid: f.refid, type: f.type, changeType: 'added' })
      continue
    }
    const changed = changedProps(baseMap.get(key)!.component, f.component)
    if (changed.length > 0) {
      diffs.push({ refid: f.refid, type: f.type, changeType: 'modified', changedProps: changed })
    }
  }

  return diffs
}
